<template>
  <config-base class="opc-zdocs-config" @view-details="showPlanDetailDialog" :config-plan-tile-status="configPlanTileStatus">
    <template #topAlert>
      <zm-banner class="opc-base-alert" type="info" :show-close="false" v-if="showAlertDesc">
        <p class="opc-base-alert__message" v-html="licenseAlertDesc" />
      </zm-banner>
    </template>
    <template #config>
      <div>
        <div>
          <span class="opc-zdocs-config__item-label">{{ $t('billing.config.select_number_license') }}</span>
        </div>
        <license-tip
          :type="planType"
          :number="licenseNumber"
          :additionalSpans="additionalSpans"
          @click="handleLicenseTipsClick"
        />
        <input-number-with-popover
          ref="userInput"
          size="lg"
          controls-position="both"
          class="gap8"
          v-model="licenseNumber"
          :min="minLicenses"
          :max="maxLicenses"
          :decrease-tips="decreaseTips"
          :label="`${title} select the number of licenses`"
          :dataTracking="{ buyInteraction: 'number of licenses', planName: trackingPlanName }"
          :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
          @change="updatePlanLicense"
        />
        <bought-license :type="planTypes.ZOOM_DOCS"/>
        <zm-banner show-close v-if="mx_downSellLicenseErrorMessage" type="info" class="gap16 opc-zdocs-config__license-alert">
          {{ mx_downSellLicenseErrorMessage }}
        </zm-banner>
        <config-cycle
          v-model="billingCycle"
          :show-monthly-price-from-cycle="true"
          :billing-cycle-list="billingCycleList"
          @switchCycleAction="updateBillingCycle"
        />
        <preview-payment class="gap24" />
      </div>
    </template>
    <template #footer v-if="canRmv">
      <zm-button data-testid="checkout-button-126" type="link" ghost @click="removePlanByKey" :aria-label="`remove ${title} plan`">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script lang="jsx">
import { get, chain, includes } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';

import ConfigBase from '@/components/config-base.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import LicenseTip from '../../ZOOM_MEETING/license-tip.vue';

import { ZOOM_DOCS_MIN_LICENSE, ZOOM_DOCS_MAX_LICENSE, planTypes } from '@/common/constants.js';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';
import { translateToEn } from '@/plugins/i18n';

import plansMixin from '../../mx/plan.js';
import addonMixin from '../../mx/addon.js';
import planDetailsDialogMixin from '../components/plan-details/plan-details-dialog.js';
import zoomDocsMixin from '../mixin.js';
import { isLastPlanTypeInPlantypeList } from '@/utils';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';

const getParentCode4DocsPlusFeatureList = () => {
  return JSON.parse(get(window, 'newBillingConfig.parentCode4DocsPlusFeatureList', '[]'));
};

const ConfigBaseComp = {
  name: 'ZoomDocsBase',
  extends: ConfigBase,
  mixins: [zoomDocsMixin],
  key: planTypes[planTypes.ZOOM_DOCS],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
    };
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `${this.trackingPlanKey} - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
  },
};

export default {
  name: 'ZoomDocsConfig',
  mixins: [zoomDocsMixin, plansMixin, addonMixin, planDetailsDialogMixin],
  components: {
    ConfigBase: ConfigBaseComp,
    ConfigCycle,
    InputNumberWithPopover,
    PreviewPayment,
    LicenseTip,
    BoughtLicense,
  },
  data() {
    return {
      billingCycle: '',
      licenseNumber: 1,
      planTypes
    };
  },
  computed: {
    ...mapGetters([
      'getTypeGroupSelectedPlans',
      'getSelectedPlans',
      'basePlanTotalLicenses',
      'getUserCurrentSubscriptions',
      'basicConfig',
      'currentEditPlan',
      'boughtPlans',
    ]),
    additionalSpans() {
      return {
        [planTypes.ZOOM_DOCS]: [
          {
            value: ZOOM_DOCS_MIN_LICENSE,
            default: true,
            text: '',
          },
          {
            value: this.maxLicenses,
            text: this.$t('billing.zone.max_of_plans_user', [this.maxLicenses, this.maxLicenses + 1]),
          },
        ],
      };
    },
    canRmv() {
      return this.$context.is.winback
        ? !isLastPlanTypeInPlantypeList(
            this.planType,
            this.getSelectedPlans.map((p) => p.type),
          )
        : this.isShowRemoveBtn;
    },
    showAlertDesc() {
      return (
        (this.$context.is.add2cart || this.$context.is.trial2paid) && getParentCode4DocsPlusFeatureList().length > 0
      );
    },
    licenseAlertDesc() {
      return this.showAlertDesc ? this.$t('billing.zoom_docs.current_account_includes_zoom_docs') : '';
    },
    showMaxLicenseTips() {
      return this.licenseNumber >= ZOOM_DOCS_MAX_LICENSE;
    },
    selectedPlan() {
      // * get zoom docs plan
      return this.getTypeGroupSelectedPlans[this.planType]?.[0];
    },

    disableDownSell() {
      // Check account MRR > 500
      return this.basicConfig?.downSellData?.disableDownSell;
    },

    previousQuantity() {
      return this.currentEditPlan?.cartInfo?.selected_number ?? ZOOM_DOCS_MIN_LICENSE;
    },

    minLicenses() {
      return this.disableDownSell || this.decreaseTips ? this.previousQuantity : ZOOM_DOCS_MIN_LICENSE;
    },

    maxLicenses() {
      const quantity = this?.currentEditPlan?.cartInfo?.selected_number ?? ZOOM_DOCS_MAX_LICENSE;
      return quantity <= ZOOM_DOCS_MAX_LICENSE ? ZOOM_DOCS_MAX_LICENSE : quantity;
    },

    decreaseTips() {
      if (this.licenseNumber === this.previousQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }

      return '';
    },

    /**
     * implementing within plansMixin
     * disable billing cycle
     */
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = get(this.selectedPlan, 'billingCycleDependBasePlanTypeList', []);

      return chain(this.getUserCurrentSubscriptions)
        .filter((p) => includes(dependCyclePlanList, p.type))
        .filter(p => !p.cancelled)
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicense() {
      return (billingCycle) => {
        return (
          this.isEditFlow &&
          this.basicConfig?.isBulkLicenseAccount &&
          this.currentEditPlan?.billing_cycle !== billingCycle
        );
      };
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },

    trackingPlanName() {
      return getTrackingProductNameByPlanType(planTypes.ZOOM_DOCS);
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    /**
     * update license number
     * @param {number} newLicense
     */
    updatePlanLicense(newLicense) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense);
    },

    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction(
        'billing cycle',
        `${this.trackingPlanName} - toggled billed ${newPlan.billing_cycle}`,
      );

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },

    handleLicenseTipsClick(e) {
      if (e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName} - the maximum amount is ${ZOOM_DOCS_MAX_LICENSE} licenses - contact sales`,
        );
      }
    },
  },
  watch: {
    showAlertDesc: {
      immediate: true,
      handler(val) {
        val &&
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName} - ${translateToEn('billing.zoom_docs.current_account_includes_zoom_docs')
              .slice(0, -1)
              .toLowerCase()} - impression`,
          );
      },
    },
    showMaxLicenseTips: {
      immediate: true,
      handler(val) {
        val &&
          this.$tracking.buyInteraction(
            `banner - product level`,
            `${this.trackingPlanName} - the maximum amount is ${ZOOM_DOCS_MAX_LICENSE} licenses - impression`,
          );
      },
    },
  },
  created() {
    if (this.selectedPlan) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = this.selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/zoom-card';

$sub-font-color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
$main-font-color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

:deep(.gap8) {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.opc-zdocs-config {
  /* Changed /deep/ to ::v-deep for Vue3 compatibility */
  :deep {
    .opc-zm-base-card {
      &__name {
        text-transform: none;
      }
      &__main--desc__content {
        font-size: 16px;
        color: $sub-font-color;
      }
    }
  }
  .opc-base-alert {
    display: flex;
    align-items: center;
  }

  &__item-label {
    font-size: 16px;
    font-weight: 600;
    color: $main-font-color;
    line-height: 20px;
  }

  &__license-alert {
    padding: 16px;
    color: $sub-font-color;
    font-size: 14px;
    line-height: 21px;
    a {
      font-weight: 450;
    }
  }
}
</style>
